import { validatePhoneNumber } from '@orn/shared-web/components/Input/PhoneNumberInput';
import validateEmail from '@orn/shared-web/lib/validation/emailValidation';

import { HousingOwnership, ResidenceType } from '../../../__generated__/globalTypes';
import { parseIsoToDateOfBirth, toIsoDate } from '../../../lib/dependants/helpers';
import { isCompleteAddress } from '../../../lib/dependants/residenceAddress';

import {
  buildResidenceInputs,
  hydrateFormState,
  isResidenceCascadeComplete,
  type IFrequentPlace,
  type IResidenceFormState,
} from './residenceForm';

import type {
  CaregiverRelationshipKind,
  UpdateCaregiverRelationshipInput,
  UpdateDependantInput,
  UpsertResidenceInput,
} from '../../../__generated__/globalTypes';
import type { IDependantResidences } from '../../../hooks/queries/useDependantResidences';
import type { IDependant } from '../../../hooks/queries/useDependants';
import type { IDateOfBirthValue } from '@orn/shared-web/components/DateOfBirthInput';

/**
 * Which residence editor a card renders. `referential` = the second dependant
 * of a cohabiting pair — they live with the other dependant, whose card owns the
 * residence, so a referential card emits no residence of its own.
 */
export type ResidenceMode = 'main' | 'referential';

/** Editable per-dependent card state. The residence slice is the co-located `IResidenceFormState` sub-form. */
export interface IDependantCardState {
  firstName: string;
  lastName: string;
  preferredName: string;
  relationship: CaregiverRelationshipKind | null;
  isStep: boolean;
  isInLaw: boolean;
  dateOfBirth: IDateOfBirthValue | null;
  email: string;
  phone: string;
  residence: IResidenceFormState;
}

/**
 * Seed editable state from a persisted dependant + their reachable residences.
 * The residence slice goes through `hydrateFormState`, which only adopts a
 * primary the dependant actually owns (never a partner's or the caregiver's
 * resolved primary — that would reparent another member's row on save).
 */
export const seedCardState = (dep: IDependant, residences: IDependantResidences | null): IDependantCardState => {
  // A checkbox can't represent "unanswered" — coerce a null lives-with-caregiver
  // to `false` BEFORE hydrating so an owned residence actually hydrates (the
  // shared `hydrateFormState` only adopts a primary when the answer is an
  // explicit `false`) and a picked address saves.
  const residence = hydrateFormState(dep.id, dep.livesWithCaregiver ?? false, residences);
  return {
    firstName: dep.firstName,
    lastName: dep.lastName,
    preferredName: dep.preferredName ?? '',
    relationship: dep.relationship,
    isStep: dep.isStep,
    isInLaw: dep.isInLaw,
    dateOfBirth: parseIsoToDateOfBirth(dep.dateOfBirth),
    email: dep.email ?? '',
    phone: dep.phoneNumber ?? '',
    residence,
  };
};

/** The mutation inputs a single card needs at save time — only the changed slices are present. Residence deletes are computed once at the form level. */
export interface ICardInputs {
  dependant?: UpdateDependantInput;
  relationship?: UpdateCaregiverRelationshipInput;
  contact?: UpdateDependantInput;
  residence?: UpsertResidenceInput;
}

/**
 * Diff one card's edited state against its saved baseline and produce only the
 * mutation inputs that changed. Identity (name + dob) and contact (email + phone)
 * map to `updateDependant`; relationship kind/step/in-law AND the lives-with-you
 * answer map to `updateCaregiverRelationship`; a changed Main-mode living
 * situation maps to an `upsertResidence`. Referential cards never emit a
 * residence (they live with the other dependant, whose card owns it). Returns
 * `{}` when nothing changed.
 */
export const buildCardInputs = (
  dep: IDependant,
  mode: ResidenceMode,
  state: IDependantCardState,
  baseline: IDependantCardState
): ICardInputs => {
  const inputs: ICardInputs = {};

  const identityChanged =
    state.firstName.trim() !== baseline.firstName.trim() ||
    state.lastName.trim() !== baseline.lastName.trim() ||
    state.preferredName.trim() !== baseline.preferredName.trim() ||
    toIsoDate(state.dateOfBirth) !== toIsoDate(baseline.dateOfBirth);
  if (identityChanged) {
    inputs.dependant = {
      dependantId: dep.id,
      firstName: state.firstName.trim(),
      lastName: state.lastName.trim(),
      preferredName: state.preferredName.trim() || null,
      // The `CalendarDate` scalar maps to a class type, but the wire accepts the
      // ISO string `toIsoDate` produces — cast to satisfy the generated input type.
      dateOfBirth: toIsoDate(state.dateOfBirth) as unknown as UpdateDependantInput['dateOfBirth'],
    };
  }

  const relationshipChanged =
    state.relationship !== baseline.relationship ||
    state.isStep !== baseline.isStep ||
    state.isInLaw !== baseline.isInLaw;
  const livesWithChanged = state.residence.liveWithCaregiver !== baseline.residence.liveWithCaregiver;
  if ((relationshipChanged && state.relationship) || livesWithChanged) {
    const relationship: UpdateCaregiverRelationshipInput = { caregiverRelationshipId: dep.caregiverRelationshipId };
    if (relationshipChanged && state.relationship) {
      relationship.relationship = state.relationship;
      relationship.isStep = state.isStep;
      relationship.isInLaw = state.isInLaw;
    }
    if (livesWithChanged && state.residence.liveWithCaregiver !== null) {
      relationship.livesWithCaregiver = state.residence.liveWithCaregiver;
    }
    inputs.relationship = relationship;
  }

  const contactChanged = state.email.trim() !== baseline.email.trim() || state.phone.trim() !== baseline.phone.trim();
  if (contactChanged) {
    inputs.contact = {
      dependantId: dep.id,
      email: state.email.trim() || null,
      phoneNumber: state.phone.trim() || null,
    };
  }

  // Residence upsert only in Main mode, and only when the built input actually
  // differs from the hydrated baseline — an untouched hydrated address re-builds
  // to the same row and must not mark the card dirty or re-fire a mutation.
  if (mode === 'main') {
    const [next] = buildResidenceInputs({ pathAForm: { dependantId: dep.id, form: state.residence } });
    const [base] = buildResidenceInputs({ pathAForm: { dependantId: dep.id, form: baseline.residence } });
    if (next && JSON.stringify(next) !== JSON.stringify(base ?? null)) {
      inputs.residence = next;
    }
  }

  return inputs;
};

/**
 * A card is savable when its required identity + relationship fields are valid
 * and the optional email + phone are blank or well-formed. The residence is only
 * validated when an actual upsert would be produced — Main mode, not living with
 * the caregiver, with an address picked — so a name-only edit on a dependant
 * with no address never blocks Save (a softer gate than onboarding's). The one
 * null-address case that does block: a persisted primary whose text was typed
 * over without picking a replacement (see `isResidenceSavable`).
 */
export const isCardValid = (
  mode: ResidenceMode,
  state: IDependantCardState,
  isValidDob: (dob: IDateOfBirthValue) => boolean
): boolean =>
  state.firstName.trim() !== '' &&
  state.lastName.trim() !== '' &&
  state.relationship !== null &&
  (state.dateOfBirth === null || isValidDob(state.dateOfBirth)) &&
  (state.email.trim() === '' || validateEmail(state.email).isValid) &&
  validatePhoneNumber(state.phone, false, true).isValid &&
  isResidenceSavable(mode, state.residence);

const isResidenceSavable = (mode: ResidenceMode, residence: IResidenceFormState): boolean => {
  // Referential / lives-with-caregiver → no upsert is produced, nothing to validate.
  if (mode !== 'main' || residence.liveWithCaregiver !== false) {
    return true;
  }
  if (residence.address === null) {
    // Editing a saved address nulls the structured pick. Saving in that state
    // would drop the persisted primary from the keep-set and silently delete the
    // home — block until the user picks a replacement or clears the text (an
    // explicit clear is an intentional delete via the orphan diff).
    return !(residence.primaryResidenceId !== null && residence.addressText.trim() !== '');
  }
  return (
    isCompleteAddress(residence.address) &&
    residence.address.addressLine1 != null &&
    isResidenceCascadeComplete(residence)
  );
};

/** Live errors for the linked living-situation fields — see {@link residenceFieldErrors}. */
export interface IResidenceFieldErrors {
  address?: string;
  residenceType?: string;
  residenceTypeOther?: string;
  homeOwnership?: string;
  homeOwnershipOther?: string;
}

/**
 * Live errors for the living-situation unit. The address and the residence-type
 * cascade persist as ONE `upsertResidence` — none of it saves until every
 * engaged piece is filled — so each missing piece names what it's holding up.
 * An untouched unit (no address, no type) shows nothing: a dependant without a
 * residence is a valid state.
 */
export const residenceFieldErrors = (mode: ResidenceMode, residence: IResidenceFormState): IResidenceFieldErrors => {
  // Referential / lives-with-caregiver cards don't render the editor.
  if (mode !== 'main' || residence.liveWithCaregiver !== false) {
    return {};
  }
  const errors: IResidenceFieldErrors = {};
  const addressPicked = residence.address !== null;

  if (!addressPicked && residence.addressText.trim() !== '') {
    errors.address = 'Select an address from the suggestions to save it';
  } else if (!addressPicked && residence.residenceType !== null) {
    errors.address = 'Add an address to save this living situation';
  }
  if (addressPicked && residence.residenceType === null) {
    errors.residenceType = 'Select a living situation to save this address';
  }
  if (residence.residenceType === ResidenceType.Other && residence.residenceTypeOther.trim() === '') {
    errors.residenceTypeOther = 'Add a description to save this living situation';
  }
  if (residence.residenceType === ResidenceType.IndependentLiving) {
    if (residence.homeOwnership === null) {
      errors.homeOwnership = 'Select home ownership to save this living situation';
    } else if (residence.homeOwnership === HousingOwnership.Other && residence.homeOwnershipOther.trim() === '') {
      errors.homeOwnershipOther = 'Add a description to save this ownership';
    }
  }
  return errors;
};

/**
 * True for a saved frequent-location row whose structured pick was typed over
 * but not yet replaced. Such a row builds no upsert, so the orphan-delete diff
 * would sweep it — callers must keep its id until the user picks a replacement
 * or clears the text (an explicit clear is an intentional delete).
 */
export const isMidEditFrequentPlace = (place: IFrequentPlace): boolean =>
  place.id !== null && place.address === null && place.text.trim() !== '';

/**
 * The frequent-location rows a given card renders: every saved row (the
 * household-shared list) plus this card's own unsaved drafts. Another card's
 * drafts stay hidden here until a save persists them into the shared list.
 */
export const visibleFrequentPlaces = (places: readonly IFrequentPlace[], dependantId: string): IFrequentPlace[] =>
  places.filter((p) => p.id !== null || p.draftFor === undefined || p.draftFor === dependantId);

/**
 * Merge one card's edited visible rows back into the full household list,
 * preserving the other cards' unsaved drafts the card never saw.
 */
export const mergeFrequentPlaces = (
  places: readonly IFrequentPlace[],
  dependantId: string,
  nextVisible: readonly IFrequentPlace[]
): IFrequentPlace[] => [
  ...nextVisible,
  ...places.filter((p) => p.id === null && p.draftFor !== undefined && p.draftFor !== dependantId),
];
